import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import { styled } from '@mui/material/styles';
import Autocomplete from '@mui/material/Autocomplete';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Loading from 'src/components/common/Loading';
import {
  v2GetDivisions,
  v2SessionInfo,
  v2SwitchDivision,
  type DivisionMembership
} from 'src/store/modules/auth/v2Operations';
import {
  buildReferrer,
  getAppUrlForReferrer,
  getStageFromHostname,
  resolveStageForApp
} from 'src/utils/ssoReferrer';
import { ALWAYS_AVAILABLE_APPS, getAppEntry, openableApps, type AppCatalogEntry } from 'src/utils/appCatalog';

/**
 * The launcher shown at the SSO host when a signed-in user arrives with no app
 * context — i.e. they typed signin.stackline.com rather than being bounced here
 * by an app. Any visit carrying a `?referrer` keeps the existing
 * straight-through redirect, so no current flow gains a click.
 *
 * Access is app × workspace: the same person can hold Atlas in one division and
 * Shopper OS in another. This leads with apps and treats the workspace as a
 * filter, because the large majority have exactly one division and a
 * workspace-first step would tax everyone to serve a minority. When a division
 * is the only one, its switcher is not rendered at all.
 *
 * `?denied=<appKey>` renders the subscription notice above the grid. That is
 * the recovery path for a user sent back from an app they cannot open: say what
 * went wrong once, then show what they can open instead.
 */

const INK = '#031425';
const PANEL = '#f6faff';
const BORDER = '#E5E9EE';
const MUTED = '#546379';
const ACCENT = '#46a8f6';

const FONT = 'Geist, system-ui, -apple-system, sans-serif';

/**
 * The app this user opened last, remembered per browser.
 *
 * Deliberately local: the API tracks the last-active DIVISION per app, which is
 * the opposite direction, and nothing server-side records "which app". A wrong
 * or missing value costs the user nothing — the resume card simply does not
 * render — so a per-browser guess is the right weight for it.
 */
const LAST_APP_KEY = 'sl-launcher-last-app';

const readLastApp = (): string | null => {
  try {
    return localStorage.getItem(LAST_APP_KEY);
  } catch {
    return null;
  }
};

const rememberLastApp = (appKey: string): void => {
  try {
    localStorage.setItem(LAST_APP_KEY, appKey);
  } catch {
    /* private browsing — the resume card just will not appear next time */
  }
};

/**
 * "raj@stackline.com" -> "Raj". Only a fallback: it is wrong for anyone whose
 * local-part is not their name. The profile name from /v2/divisions is
 * preferred, and this covers the case where the API build predates that field
 * or the user record could not be read.
 */
const firstNameFromEmail = (email: string): string => {
  const local = email.split('@')[0] || '';
  const first = local.split(/[._-]/)[0] || '';
  return first ? first.charAt(0).toUpperCase() + first.slice(1) : '';
};

/** "Raj Ramasamy" -> "RR"; a single name yields one letter. */
const initialsFrom = (name: string, email: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (email[0] || '?').toUpperCase();
};

const greetingFor = (hour: number): string =>
  hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

const Page = styled('div')({
  fontFamily: FONT,
  minHeight: '100%',
  background: '#fbfcfe',
  color: INK,
  display: 'flex',
  flexDirection: 'column'
});

/**
 * Two soft washes rather than a flat band. This is the only decorative surface
 * on the page; everything below it is white on near-white.
 */
const Hero = styled('div')({
  background: [
    'radial-gradient(120% 140% at 12% 0%, #e9f2ff 0%, rgba(233,242,255,0) 55%)',
    'radial-gradient(90% 120% at 92% 8%, #eef4fb 0%, rgba(238,244,251,0) 60%)',
    '#fbfcfe'
  ].join(','),
  padding: '72px 56px 56px',
  '@media (max-width: 700px)': { padding: '40px 20px 32px' }
});

const Inner = styled('div')({ width: '100%', maxWidth: 1120, margin: '0 auto' });

const SectionLabel = styled('div')({
  fontSize: 13,
  fontWeight: 600,
  color: MUTED,
  letterSpacing: '0.4px',
  textTransform: 'uppercase'
});

/** The one dark surface on the page, so it reads as THE thing to click. */
const Resume = styled('button')({
  display: 'flex',
  alignItems: 'center',
  gap: 28,
  width: '100%',
  background: INK,
  border: `1px solid ${INK}`,
  borderRadius: 18,
  padding: '28px 32px',
  color: '#ffffff',
  font: 'inherit',
  textAlign: 'left',
  cursor: 'pointer',
  transition: 'box-shadow .14s ease, transform .14s ease',
  '&:hover': { boxShadow: '0 14px 34px rgba(3,20,37,.20)', transform: 'translateY(-3px)' },
  '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2 },
  '&:disabled': { opacity: 0.7, cursor: 'wait', transform: 'none', boxShadow: 'none' },
  '@media (max-width: 700px)': { flexDirection: 'column', alignItems: 'flex-start', gap: 18 }
});

const TopBar = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '20px 40px',
  borderBottom: `1px solid ${BORDER}`,
  background: '#ffffff'
});

const Body = styled('div')({
  flexGrow: 1,
  display: 'flex',
  justifyContent: 'center',
  padding: '56px 40px 64px',
  '@media (max-width: 700px)': { padding: '28px 20px 40px' }
});

const Column = styled('div')({
  width: '100%',
  maxWidth: 1040,
  display: 'flex',
  flexDirection: 'column',
  gap: 28
});

const Grid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: 18,
  '@media (max-width: 1000px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
  '@media (max-width: 640px)': { gridTemplateColumns: 'minmax(0, 1fr)' }
});

const Card = styled('button')({
  display: 'flex',
  flexDirection: 'column',
  background: '#ffffff',
  border: `1px solid ${BORDER}`,
  borderRadius: 18,
  padding: 0,
  overflow: 'hidden',
  minHeight: 212,
  font: 'inherit',
  color: 'inherit',
  textAlign: 'left',
  cursor: 'pointer',
  transition: 'border-color .14s ease, box-shadow .14s ease, transform .14s ease',
  '&:hover': { borderColor: '#cfd7df', boxShadow: '0 10px 30px rgba(3,20,37,.10)', transform: 'translateY(-3px)' },
  '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2 },
  '&:disabled': { opacity: 0.6, cursor: 'wait', transform: 'none', boxShadow: 'none' }
});

const Avatar = styled('button')({
  width: 34,
  height: 34,
  borderRadius: 999,
  background: INK,
  color: '#ffffff',
  border: 'none',
  font: 'inherit',
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: '0.2px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'box-shadow .12s ease',
  '&:hover': { boxShadow: '0 0 0 3px rgba(3,20,37,.10)' },
  '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2 }
});

/** One field colour for every product — the brand has no per-product palette. */
const MarkField = styled('div')({
  background: '#F1F5FA',
  padding: 26,
  display: 'flex',
  alignItems: 'center',
  minHeight: 104,
  width: '100%'
});

const Chip = styled('button')({
  font: 'inherit',
  fontSize: 14,
  fontWeight: 500,
  letterSpacing: '0.13px',
  color: INK,
  background: '#ffffff',
  border: `1px solid ${BORDER}`,
  borderRadius: 999,
  height: 36,
  padding: '0 16px',
  cursor: 'pointer',
  transition: 'background .12s ease, border-color .12s ease',
  '&:hover': { background: '#F2F4F6' },
  '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2 }
});

/**
 * Workspace picker.
 *
 * Chips do not survive real data: a user can hold 17 memberships of which 11
 * share the division name "ECommerce Channel" (in three casings). Company is
 * the identity here -- 15 distinct companies against 7 distinct division names
 * -- so lead with it and keep the division as the qualifier underneath, which
 * is also what DivisionSelector already does.
 */
const workspacePickerSx = {
  width: 420,
  // The root owns the height and is the flex row.
  '& .MuiOutlinedInput-root': {
    height: 44,
    minHeight: 44,
    display: 'flex',
    alignItems: 'center',
    background: '#ffffff',
    borderRadius: '999px',
    padding: '0 10px 0 18px !important',
    font: 'inherit',
    fontSize: 14,
    letterSpacing: '0.13px',
    color: INK
  },
  '& .MuiOutlinedInput-input': {
    height: 'auto',
    // MUI ships `margin-bottom: 7px` here; with align-items:center the MARGIN
    // box is what gets centred, so the text sat 3.5px high.
    margin: '0 !important',
    padding: '0 !important',
    lineHeight: '20px',
    fontFamily: FONT
  },
  // Take the caret out of absolute positioning and let the row centre it.
  // MUI pins it with `top: calc(50% - 14px)`, which assumes a 28px adornment;
  // ours measures 53px, so that formula lifts it clear of the field. Its rule
  // has the same specificity as this one and can win on injection order, hence
  // !important rather than a more specific selector.
  '& .MuiAutocomplete-endAdornment': {
    position: 'static !important',
    transform: 'none !important',
    display: 'flex',
    alignItems: 'center',
    height: 28,
    flexShrink: 0
  },
  '& .MuiAutocomplete-popupIndicator': { padding: 4, color: MUTED },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: BORDER },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cfd7df' },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ACCENT, borderWidth: 1 }
};


const Search = styled('input')({
  font: 'inherit',
  fontSize: 14,
  letterSpacing: '0.13px',
  color: INK,
  background: '#ffffff',
  border: `1px solid ${BORDER}`,
  borderRadius: 999,
  height: 44,
  width: 260,
  padding: '0 20px 0 42px',
  '&::placeholder': { color: '#96a5b3' },
  '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2 }
});

const Notice = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 14,
  background: '#ffffff',
  border: '1px solid #f5c2ce',
  borderLeft: '3px solid #f35379',
  borderRadius: 12,
  padding: '18px 20px'
});

/** Company is the identity; the division name repeats across companies. */
const workspaceLabel = (d: DivisionMembership): string => d.companyName || d.name || d.id;

const AppMark: React.FC<{ entry: AppCatalogEntry; size?: number }> = ({ entry, size = 28 }) => {
  const { Logo } = entry;
  if (Logo) {
    // Brandclub OS ships only a wordmark (132x20), so it is width-bound rather
    // than height-bound; the square marks are height-bound.
    const isWordmark = entry.name === 'Brandclub OS';
    return (
      <Logo
        style={
          isWordmark
            ? { height: Math.round(size * 0.46), width: 'auto', maxWidth: size * 4.7, display: 'block' }
            : { height: size, width: size, display: 'block' }
        }
      />
    );
  }
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        borderRadius: 8,
        background: entry.color,
        color: '#ffffff',
        fontSize: 13,
        fontWeight: 600
      }}
    >
      {entry.letter}
    </div>
  );
};

const AppLauncher: React.FC<RouteComponentProps> = ({ history }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [divisions, setDivisions] = useState<DivisionMembership[]>([]);
  const [activeId, setActiveId] = useState<string>('');
  const [selectedId, setSelectedId] = useState<string>('');
  const [query, setQuery] = useState('');
  const [opening, setOpening] = useState<string | null>(null);
  const [profile, setProfile] = useState<{ name: string; email: string } | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const deniedKey = useMemo(() => new URLSearchParams(window.location.search).get('denied') || '', []);
  const email = useMemo(() => {
    try {
      return localStorage.getItem('x-sl-signin-user-email') || '';
    } catch {
      return '';
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const info = await v2SessionInfo();
        if (!info.authenticated) {
          // No session — the sign-in form owns `/`.
          history.replace('/');
          return;
        }
        const { divisions: divs, activeDivisionId, user } = await v2GetDivisions();
        const list = divs || [];
        if (user && (user.name || user.email)) {
          setProfile({ name: (user.name || '').trim(), email: user.email || '' });
        }
        setDivisions(list);
        const active = activeDivisionId || list[0]?.id || '';
        setActiveId(active);
        setSelectedId(active);
      } catch {
        setError('We could not load your workspaces. Please refresh and try again.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [history]);

  // Navigating away leaves `opening` set. Chrome's back/forward cache restores
  // the page with its JS state intact rather than remounting, so pressing Back
  // returned to a permanently busy card. `pageshow` with persisted=true is the
  // only signal that a restore (rather than a load) happened.
  useEffect(() => {
    const onShow = (e: PageTransitionEvent) => {
      if (e.persisted) setOpening(null);
    };
    window.addEventListener('pageshow', onShow);
    return () => window.removeEventListener('pageshow', onShow);
  }, []);

  const selected = divisions.find((d) => d.id === selectedId);

  // Alphabetical by company so a 17-membership list is scannable.
  const sortedDivisions = useMemo(
    () => [...divisions].sort((a, b) => workspaceLabel(a).localeCompare(workspaceLabel(b))),
    [divisions]
  );

  const displayEmail = profile?.email || email;
  const displayName = profile?.name || '';
  const firstName = useMemo(
    () => (displayName ? displayName.trim().split(/\s+/)[0] : firstNameFromEmail(displayEmail)),
    [displayName, displayEmail]
  );
  const greeting = useMemo(() => {
    const hello = greetingFor(new Date().getHours());
    return firstName ? `${hello}, ${firstName}` : hello;
  }, [firstName]);
  const initials = useMemo(() => initialsFrom(displayName, displayEmail), [displayName, displayEmail]);

  // Only offer to resume an app the CURRENT workspace can actually open —
  // otherwise the card is a promise the click cannot keep.
  const lastApp = useMemo(() => {
    const key = readLastApp();
    if (!key) return null;
    return openableApps(selected?.appAccess, { include: ALWAYS_AVAILABLE_APPS }).find((a) => a.key === key) || null;
  }, [selected]);

  const apps = useMemo(() => {
    const q = query.trim().toLowerCase();
    return openableApps(selected?.appAccess, { include: ALWAYS_AVAILABLE_APPS }).filter(
      (a) => !q || a.name.toLowerCase().includes(q)
    );
  }, [selected, query]);

  const open = useCallback(
    async (appKey: string, division: DivisionMembership) => {
      setOpening(appKey);
      setError(null);
      try {
        rememberLastApp(appKey);
        const stage = getStageFromHostname(window.location.hostname);
        const referrer = buildReferrer(appKey, resolveStageForApp(appKey, stage));
        // Opening an app in another workspace has to move the session first --
        // the target app reads the division off the cookie, not the URL.
        if (division.id !== activeId) {
          await v2SwitchDivision(division.id, referrer);
        }
        window.location.href = getAppUrlForReferrer(referrer);
      } catch {
        setError('We could not open that app. Please try again.');
        setOpening(null);
      }
    },
    [activeId]
  );

  if (loading) return <Loading />;

  const denied = deniedKey ? getAppEntry(deniedKey) : null;
  const multipleWorkspaces = divisions.length > 1;

  return (
    <Page>
      <TopBar>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.2px' }}>Stackline</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {displayName && (
            <span style={{ fontSize: 14, fontWeight: 500, letterSpacing: '0.13px' }}>{displayName}</span>
          )}
          <Avatar
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            aria-label="Account"
            aria-haspopup="menu"
            aria-expanded={!!menuAnchor}
          >
            {initials}
          </Avatar>
          <Menu
            anchorEl={menuAnchor}
            open={!!menuAnchor}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{
              paper: {
                sx: { fontFamily: FONT, borderRadius: '12px', marginTop: '8px', minWidth: 240, border: `1px solid ${BORDER}` }
              }
            }}
          >
            {/* Identity, then the action. A div rather than a MenuItem so it is
                not focusable or clickable — it is a label, not a choice. */}
            <div style={{ padding: '12px 16px 12px', borderBottom: `1px solid ${BORDER}` }}>
              {displayName && (
                <div style={{ fontSize: 14, fontWeight: 600, color: INK, letterSpacing: '0.13px' }}>{displayName}</div>
              )}
              <div style={{ fontSize: 13, color: MUTED, letterSpacing: '0.13px', marginTop: displayName ? 2 : 0 }}>
                {displayEmail}
              </div>
            </div>
            <MenuItem
              component="a"
              href="/sign_out"
              sx={{ fontFamily: FONT, fontSize: 14, color: INK, padding: '11px 16px', marginTop: '4px' }}
            >
              Sign out
            </MenuItem>
          </Menu>
        </div>
      </TopBar>

      <Hero>
        <Inner>
          <div style={{ fontSize: 15, color: MUTED, letterSpacing: '0.13px' }}>{greeting}</div>
          <h1 style={{ margin: '8px 0 0', fontSize: 46, lineHeight: 1.08, fontWeight: 700, letterSpacing: '-1.2px', maxWidth: '15ch' }}>
            {denied ? 'Open another app' : 'Your retail growth platform, all in one place.'}
          </h1>
          <p style={{ margin: '16px 0 0', fontSize: 16, color: MUTED, maxWidth: '56ch', lineHeight: 1.6 }}>
            {denied
              ? 'These are the apps your access does cover.'
              : selected
              ? `Everything below is live for ${workspaceLabel(selected)}.`
              : 'Everything below is live for your workspace.'}
          </p>

          {multipleWorkspaces && (
            <div style={{ marginTop: 36, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: MUTED, letterSpacing: '0.13px' }}>Workspace</span>
              <Autocomplete<DivisionMembership, false, true, false>
                sx={workspacePickerSx}
                options={sortedDivisions}
                value={selected}
                onChange={(_e, next) => next && setSelectedId(next.id)}
                disableClearable
                openOnFocus
                isOptionEqualToValue={(a, b) => a.id === b.id}
                getOptionLabel={(div) =>
                  div.name && div.name !== workspaceLabel(div)
                    ? `${workspaceLabel(div)} — ${div.name}`
                    : workspaceLabel(div)
                }
                slotProps={{ paper: { sx: { fontFamily: FONT, borderRadius: '12px', marginTop: '6px' } } }}
                renderOption={(props, div) => {
                  const count = openableApps(div.appAccess, { include: ALWAYS_AVAILABLE_APPS }).length;
                  return (
                    <li {...props} key={div.id} style={{ display: 'block', padding: '10px 16px' }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: INK, letterSpacing: '0.13px' }}>
                        {workspaceLabel(div)}
                      </div>
                      <div style={{ fontSize: 12, color: MUTED, letterSpacing: '0.13px', marginTop: 2 }}>
                        {div.name}
                        {' · '}
                        {count === 1 ? '1 app' : `${count} apps`}
                      </div>
                    </li>
                  );
                }}
                renderInput={(params) => <TextField {...params} placeholder="Search workspaces" aria-label="Workspace" />}
              />
            </div>
          )}
        </Inner>
      </Hero>

      <div style={{ padding: '8px 56px 76px' }}>
        <Inner>
          {denied && (
            <Notice style={{ marginBottom: 8 }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f35379" strokeWidth="1.8" strokeLinecap="round" style={{ flexShrink: 0, marginTop: 1 }} aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 8v4.5" />
                <path d="M12 16h.01" />
              </svg>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: '0.13px' }}>
                  You don&apos;t have access to {denied.name}
                </div>
                <div style={{ fontSize: 14, color: MUTED, lineHeight: 1.5, letterSpacing: '0.13px' }}>
                  {selected
                    ? `${workspaceLabel(selected)} isn't subscribed to ${denied.name}.`
                    : `Your account isn't subscribed to ${denied.name}.`}{' '}
                  Open one of your other apps below, or <a href="mailto:support@stackline.com" style={{ color: ACCENT }}>contact us</a> to add it.
                </div>
              </div>
            </Notice>
          )}

          {error && (
            <Notice style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 14, color: INK, letterSpacing: '0.13px' }}>{error}</div>
            </Notice>
          )}

          {lastApp && !denied && (
            <>
              <SectionLabel style={{ margin: '24px 0 16px' }}>Pick up where you left off</SectionLabel>
              <Resume
                disabled={opening === lastApp.key}
                aria-busy={opening === lastApp.key}
                onClick={() => selected && open(lastApp.key, selected)}
                // Distinct from the grid card for the same app, which is also
                // on the page — two controls must not share an accessible name.
                aria-label={`Resume ${lastApp.name}`}
              >
                <div style={{ width: 76, height: 76, borderRadius: 16, background: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <AppMark entry={lastApp} size={46} />
                </div>
                <div style={{ flexGrow: 1 }}>
                  <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.3px' }}>{lastApp.name}</div>
                  {lastApp.blurb && (
                    <div style={{ fontSize: 14.5, color: '#9fb0c4', marginTop: 5, lineHeight: 1.5 }}>{lastApp.blurb}</div>
                  )}
                </div>
                <span style={{ fontSize: 14, fontWeight: 600, color: INK, background: '#ffffff', borderRadius: 999, padding: '12px 24px', flexShrink: 0 }}>
                  Open
                </span>
              </Resume>
            </>
          )}

          {apps.length > 0 && (
            <>
              <SectionLabel style={{ margin: lastApp && !denied ? '44px 0 16px' : '24px 0 16px' }}>
                Everything you can open
              </SectionLabel>
              <Grid>
                {apps.map((app) => (
                  <Card
                    key={app.key}
                    disabled={opening === app.key}
                    aria-busy={opening === app.key}
                    onClick={() => selected && open(app.key, selected)}
                    aria-label={`Open ${app.name}`}
                  >
                    <MarkField>
                      <AppMark entry={app} size={34} />
                    </MarkField>
                    <div style={{ padding: '20px 24px 24px', display: 'flex', flexDirection: 'column', gap: 7 }}>
                      <div style={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.1px' }}>{app.name}</div>
                      {app.blurb && (
                        <div style={{ fontSize: 14, color: MUTED, lineHeight: 1.55 }}>{app.blurb}</div>
                      )}
                    </div>
                  </Card>
                ))}
              </Grid>
            </>
          )}

          {apps.length === 0 && (
            <div style={{ background: '#ffffff', border: `1px solid ${BORDER}`, borderRadius: 18, padding: 40, textAlign: 'center', marginTop: 24 }}>
              <div style={{ fontSize: 16, fontWeight: 600 }}>
                {selected ? `No apps in ${workspaceLabel(selected)}` : 'No apps available'}
              </div>
              <div style={{ fontSize: 14, color: MUTED, marginTop: 8, lineHeight: 1.5, letterSpacing: '0.13px' }}>
                Your access to this workspace doesn&apos;t include any apps yet. An admin on the account can grant it.
              </div>
            </div>
          )}
        </Inner>
      </div>
    </Page>
  );
};

export default AppLauncher;
